import {gaConfig} from '@stencil/hotels-config';
import {
  bookingWidgetSelector,
  paxSplittedDataSelector,
  similarHotelsSelector,
} from '@stencil/hotels-details-reducer/src/selectors';
import {defaultTheme} from '@stencil/build-theme';
// SELECTORS
import {FetchStatusType} from '@stencil/hotels-details-reducer/src/types';
import parseDateIntoHermesString from '@stencil/hotels-utils/src/date-utils/parse-date-into-hermes-string';
// UTILS
import detailsPageClickGaPush from '@stencil/hotels-utils/src/details-page-click-ga-push';
import {detailsNavigationIdsAndValues} from '@stencil/hotels-utils/src/get-details-navigation-id-and-values';
import getNoOfRoomFlavors from '@stencil/hotels-utils/src/get-no-of-room-flavors';
import getSrpUrl from '@stencil/hotels-utils/src/get-srp-url';
// CONFIG
import {
  scrollIntoViewDetails,
  scrollToEl,
} from '@stencil/hotels-utils/src/scroll-into-view-details';
import splitRoomString from '@stencil/hotels-utils/src/split-room-string';
// TYPES
import {isDate} from '@stencil/type-guards';
// COLOR PALETTE
import React, {useEffect} from 'react';
// HOOKS
import {useSelector} from 'react-redux';
import DPSoldOut from '../../../../../stories/components/hotels/DetailsPage/SoldOut/DPSoldOut';
// COMPONENTS
import Text from '../../../../../stories/core-components/TextFieldExpt/TextFieldExpt';
import DoubleBedIcon from '../../../../../stories/Icons/DoubleBedIcon';
import RightDoubleArrowIcon from '../../../../../stories/Icons/RightDoubleArrow';
// ICONS
import RupeeIcon from '../../../../../stories/Icons/RupeeIcon';
import UserIcon from '../../../../../stories/Icons/UserIcon';
// import useQuery from '../../../../hooks/useQuery';
import useCurrentRoomData from '../../../../hooks/useCurrentRoomData';
// import { scrollIntoView } from './utils/details-page-utils';
// STYLES
import {FlexCentered} from '../../../../../stories/components/CommonStyleUtils/dwebCommon.styles';
import * as S from './styles/BookingWidget.styles';

const {DetailsGAClickEventActions} = gaConfig;

export default function BookingWidget() {
  let {
    roomsDataFromHermes = [],
    roomString,
    isHermesLoading,
    hasHermesData,
    checkin,
    checkout,
    hotelId,
    cityName,
    cityId,
    country,
  } = useSelector(bookingWidgetSelector);

  const similarHotelsData = useSelector(similarHotelsSelector);
  const isSimilarHotelsFetchCompleted =
    similarHotelsData?.fetchStatus === FetchStatusType.RESOLVED ||
    similarHotelsData?.fetchStatus === FetchStatusType.REJECTED;

  const thereAreSimilarHotels = !!similarHotelsData?.list.slice(1)?.length;

  const paxSplittedRoomString = useSelector(paxSplittedDataSelector);

  if (paxSplittedRoomString) {
    roomString = paxSplittedRoomString;
  }

  const {roomCount, paxCount, childCount} = splitRoomString(roomString);

  const totalNoOfRooms =
    (roomsDataFromHermes &&
      roomsDataFromHermes.length &&
      getNoOfRoomFlavors(roomsDataFromHermes)) ||
    0;

  const {roomPrice: currentRoomPrice, tax} = useCurrentRoomData();

  const noOfRooms = roomCount;
  const noOfGuests = paxCount + childCount;

  const isSoldOut = !isHermesLoading && !!hasHermesData && !roomsDataFromHermes.length;

  useEffect(() => {
    if (isSoldOut) {
      const checkinDate = parseDateIntoHermesString(checkin.date);
      const checkoutDate = parseDateIntoHermesString(checkout.date);

      const eventLabel = hotelId + '_' + roomString + '_' + checkinDate + '_' + checkoutDate;
      // const eventLabel = `${hotelId}_${roomString}_${checkinDate}_${checkoutDate}`

      detailsPageClickGaPush({
        eventAction: DetailsGAClickEventActions.soldout.soldOut,
        eventLabel,
      });
    }
  }, [isSoldOut, hotelId, checkin, checkout, roomString]);

  let srpUrl = '';

  const destination = {
    _id: cityId,
    n: cityName,
  };

  if (
    checkin &&
    isDate(checkin.date) &&
    checkout &&
    isDate(checkout.date) &&
    roomString &&
    destination?._id &&
    destination?.n
  ) {
    srpUrl = getSrpUrl({
      checkin,
      checkout,
      roomString,
      destination: destination,
      country,
    });
  }

  const nonSoldOutUI = (
    <S.BookingWidgetStyleContainer>
      {!!currentRoomPrice && <S.PriceStartsAtText>price starts at</S.PriceStartsAtText>}

      <S.PriceInfo>
        <S.Column>
          {!!currentRoomPrice && (
            <FlexCentered>
              <RupeeIcon width="1.4rem" height="1.4rem" fill={defaultTheme.colors.downriver.base} />
              <S.PriceValue price={currentRoomPrice} />
            </FlexCentered>
          )}

          {!!tax && (
            <FlexCentered>
              +
              <RupeeIcon width=".8rem" height=".8rem" fill={defaultTheme.colors.downriver.base} />
              <S.PlusPriceValue price={tax} />
            </FlexCentered>
          )}

          {!!currentRoomPrice && <S.PerRoomPerNightLabel>per room / night</S.PerRoomPerNightLabel>}
        </S.Column>
        <S.Column>
          {!!noOfGuests && (
            <S.NoOfGuestsContainer>
              <UserIcon width="2rem" height="2rem" fill={defaultTheme.colors.downriver.medium} />
              <S.NoOfGuestsAndRoomsTextStyles>
                {noOfGuests} x Guest{noOfGuests > 1 ? 's' : ''}
              </S.NoOfGuestsAndRoomsTextStyles>
            </S.NoOfGuestsContainer>
          )}

          {!!noOfRooms && (
            <S.NoOfRoomsContainer>
              <DoubleBedIcon
                width="2rem"
                height="2rem"
                fill={defaultTheme.colors.downriver.medium}
              />
              <S.NoOfGuestsAndRoomsTextStyles>
                {noOfRooms} x Room{noOfRooms > 1 ? 's' : ''}
              </S.NoOfGuestsAndRoomsTextStyles>
            </S.NoOfRoomsContainer>
          )}
        </S.Column>
      </S.PriceInfo>

      {!!totalNoOfRooms && (
        <S.ViewRoomOptionsButton
          onClick={e => {
            detailsPageClickGaPush({
              eventAction: DetailsGAClickEventActions.viewXRoomOptionsClick,
            });
            scrollIntoViewDetails(document.querySelector(detailsNavigationIdsAndValues.rooms.id));
          }}
        >
          <Text bold customFontSize="1.6">
            <span>
              VIEW {totalNoOfRooms} ROOM OPTION{totalNoOfRooms > 1 ? 'S' : ''}
            </span>
            <S.RightDoubleArrowIconContainer>
              <RightDoubleArrowIcon
                width="2rem"
                height="2rem"
                fill={defaultTheme.colors.goWhite.base}
              />
            </S.RightDoubleArrowIconContainer>
          </Text>
        </S.ViewRoomOptionsButton>
      )}
    </S.BookingWidgetStyleContainer>
  );

  return (
    <>
      {isSoldOut ? (
        <DPSoldOut
          tryAlternateDatesClick={() => {
            detailsPageClickGaPush({
              eventAction: DetailsGAClickEventActions.soldout.showAlternateDatesClick,
            });

            const calendarEl = document.getElementById('search-widget-checkin-input');

            if (calendarEl) {
              calendarEl.click();
            }
          }}
          showSimilarHotelsClick={() => {
            detailsPageClickGaPush({
              eventAction: DetailsGAClickEventActions.soldout.showSimilarHotelsClick,
            });

            scrollToEl(detailsNavigationIdsAndValues.similarHotels.id);
          }}
          showSimilarHotelsText={thereAreSimilarHotels}
          onSeeOtherHotelsClick={() => {
            detailsPageClickGaPush({
              eventAction: DetailsGAClickEventActions.soldout.seeOtherHotelsClick,
            });
          }}
          srpUrl={thereAreSimilarHotels || !isSimilarHotelsFetchCompleted ? '' : srpUrl}
          cityName={cityName}
        />
      ) : (
        nonSoldOutUI
      )}
    </>
  );
}
